import { getIsTrainIncident } from '@rsos/base-utils';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertTypes';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import ICON_MAPPING from '../../assets/alerts/sprites-output/sprites-info.json';
import alertsSprites from '../../assets/alerts/sprites-output/sprites_2x.png';

/**
 * Util function to check if it is an AAA and if it is unaccepted. Returns boolean
 * @param {Object} alert - full alert object
 */
export const getIsUnacceptedActiveAssailant = (alert = {}) => {
  return (
    alert.emergency_type?.name === ACTIVE_ASSAILANT &&
    alert.status?.name !== ACCEPTED
  );
};

/**
 * Util function to check if it is a train alert and if it is unaccepted. Returns boolean
 * @param {Object} alert - full alert object
 */
export const getIsUnacceptedTrainIncident = (alert = {}) => {
  return (
    getIsTrainIncident(alert.emergency_type?.name, true) &&
    alert.status?.name !== ACCEPTED
  );
};

/**
 * Util function to check if it is a train alert and if it is unaccepted. Returns boolean
 * @param {Boolean} isHistoric - indication whether it is a historic marker or primary
 * @param {Boolean} isAllView - indication of which view we are in
 * @param {Object} alert - full alert object
 */
export const getAlertMarkerIconName = (
  isHistoric = false,
  isAllView = true,
  alert = {},
) => {
  const isUnacceptedActiveAssailant = getIsUnacceptedActiveAssailant(alert);
  const isUnacceptedTrainIncident = getIsUnacceptedTrainIncident(alert);
  const isTestAlert = alert?.emergency_type?.name?.startsWith('TEST');

  if (isAllView) {
    if (isUnacceptedActiveAssailant) {
      return 'allalerts_active_assailant';
    } else if (isUnacceptedTrainIncident) {
      return 'allalerts_train';
    } else if (isTestAlert) {
      return 'allalerts_test';
    }
    return 'allalerts_marker';
  } else {
    if (isHistoric) {
      return 'historic_marker';
    } else {
      if (isTestAlert) {
        return 'alert_test_selected';
      }
      if (isUnacceptedActiveAssailant) {
        return 'alert_active_assailant_selected';
      } else if (isUnacceptedTrainIncident) {
        return 'alert_train_selected';
      }
      return 'alert_marker_selected';
    }
  }
};

/**
 * Util function to return locations of an incident
 * @param {Object} incidents - alerts
 * @param {String} incidentID - alert id
 */

export const getIncidentLocations = (incidents = {}, incidentID) => {
  const incident = incidents[incidentID];
  if (incident) {
    return incident.location_history.map(location => {
      return {
        ...location.geodetic,
        created_at: location.created_at,
        alert: incident,
      };
    });
  }
  return [];
};

export const MARKER_SIZES = {
  sizeScale: 60, // this is the factor to scale the icon.
  sizeMinPixels: 38, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 30, // when zoomed in, the max height of the icon.
};

/**
 * Util function to determine alert marker opacity. It is done via getColor function as the native 'opacity'
 * attribute applies opacity to the entire layer, not an individual marker and for alerts it needs to be more granular
 * @param {String} activeQueueTab - alerts or calls
 * @param {Object} alert - alert full object
 */
export const getAlertMarkerOpactity = (activeQueueTab = '', alert = {}) => {
  const isUnacceptedActiveAssailant = getIsUnacceptedActiveAssailant(alert);
  const isUnacceptedTrainIncident = getIsUnacceptedTrainIncident(alert);

  if (
    activeQueueTab === EMERGENCY_CALLS &&
    !isUnacceptedActiveAssailant &&
    !isUnacceptedTrainIncident
  ) {
    return [255, 255, 255, 125];
  }
  return [255, 255, 255, 255];
};
/**
 * Define Marker properties
 * @param {Boolean} isAllView - isAllView or selected view
 * @param {String} activeQueueTab - alerts or calls
 * @param {String} selectedCallerID - selected caller id, if any
 */

export const getAlertsIconProps = (
  isAllView = false,
  activeQueueTab = '',
  selectedCallerID,
) => ({
  iconAtlas: alertsSprites,
  iconMapping: ICON_MAPPING,
  sizeUnits: 'meters',
  visible: !selectedCallerID,
  pickable: true,
  highlightColor: [255, 255, 255, 60],
  getIcon: d => getAlertMarkerIconName(d?.isHistoric, isAllView, d?.alert),
  getPosition: d => [d?.longitude, d?.latitude],
  getColor: d => getAlertMarkerOpactity(activeQueueTab, d?.alert),
  ...MARKER_SIZES,
});

/**
 * Define Uncertainty Radius Circles properties
 * @param {String} selectedAlertID - selected alert id, if any
 */

export const getCommonCircleLayerProps = selectedAlertID => ({
  extruded: true,
  filled: true,
  getLineWidth: 2,
  lineWidthMaxPixels: 2,
  lineWidthMinPixels: 1,
  pickable: false, // If true, the layers respond to pointer picking events
  pointType: 'circle',
  stroked: true,
  radiusMinPixels: 1,
  visible: !!selectedAlertID,
  getFillColor: () => [92, 110, 157, 65],
  getLineColor: () => [92, 110, 157, 204],
  getPosition: d => [d?.longitude, d?.latitude],
  getRadius: d => d?.uncertainty_radius,
});
